import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  Moment,
  ModulePage,
  Resourceful,
  Select,
  StatusBadge,
  TextArea,
  TextInput,
  Toolbar,
  type Column,
} from '../../shell/kit';
import { errorText } from '../../shell/errors';
import { useResource } from '../../shell/hooks';
import { useToast } from '../../shell/toast';
import { allows, sites as loadSites } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import {
  createUnit,
  fetchCycles,
  fetchUnits,
  riskTone,
  runForecast,
  startProgram,
  type Program,
  type Unit,
  type UnitKind,
  type UnitProtocol,
} from './api';
import { makeEquipmentT, type EquipmentKey, type EquipmentTranslate } from './strings';

/**
 * Раздел «Оборудование» (ТЗ §9.5, М6.8).
 *
 * Управляющий видит узлы своего объекта: состояние, последние показания
 * телеметрии и прогноз отказа на неделю с причиной — чтобы вызвать механика
 * до поломки, а не после. Кассир и оператор запускают программу мойки.
 * Администратор сети заводит узлы. Права проверяет сервер по объекту узла;
 * здесь — только то, какие кнопки показывать.
 */

const KINDS: readonly UnitKind[] = ['portal', 'tunnel', 'self_service', 'vacuum', 'water_treatment', 'compressor', 'other'];
const PROTOCOLS: readonly UnitProtocol[] = ['http', 'mqtt', 'fake'];

/** «код;название;секунды» по строке → программы. Неполные строки отбрасываются. */
export function parsePrograms(text: string): readonly Program[] {
  return text
    .split('\n')
    .map((line) => line.split(';').map((p) => p.trim()))
    .filter((parts) => parts.length === 3 && parts[0] && parts[1] && Number(parts[2]) > 0)
    .map(([code, name, seconds]) => ({ code: code!, name: name!, durationSeconds: Math.round(Number(seconds)) }));
}

function percent(risk: number): string {
  return `${Math.round(risk * 100)} %`;
}

function stateKey(state: string | null): EquipmentKey {
  if (state === 'running' || state === 'idle' || state === 'starting' || state === 'fault') {
    return `equipment.state.${state}` as EquipmentKey;
  }
  return 'equipment.state.unknown';
}

function UnitDetails({ t, unit, canStart, onChanged }: {
  t: EquipmentTranslate;
  unit: Unit;
  canStart: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const cycles = useResource(() => fetchCycles(unit.id), [unit.id, reload]);

  async function start(program: string): Promise<void> {
    setBusy(true);
    try {
      await startProgram(unit.id, program);
      toast.ok(t('equipment.started'), program);
      setReload((v) => v + 1);
      onChanged();
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {canStart && unit.protocol !== 'mqtt' && unit.programs.length > 0 ? (
        <Toolbar>
          {unit.programs.map((p) => (
            <Button key={p.code} tone="primary" disabled={busy || unit.status !== 'active'} onClick={() => void start(p.code)}>
              {t('equipment.start')}: {p.name}
            </Button>
          ))}
        </Toolbar>
      ) : null}
      <Card title={t('equipment.cycles')}>
        <Resourceful resource={cycles}>
          {(rows) =>
            rows.length === 0 ? (
              <EmptyState title={t('equipment.cycles.empty')} />
            ) : (
              <ul className="flex flex-col gap-1 text-sm">
                {rows.slice(0, 10).map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-2">
                    <Moment iso={c.requestedAt} />
                    <span className="font-medium">{c.program}</span>
                    <StatusBadge tone={c.status === 'completed' ? 'ok' : c.status === 'failed' || c.status === 'aborted' ? 'danger' : 'brand'}>
                      {t(`equipment.cycle.${c.status}` as EquipmentKey)}
                    </StatusBadge>
                    {c.error ? <span className="text-xs text-danger">{c.error}</span> : null}
                  </li>
                ))}
              </ul>
            )
          }
        </Resourceful>
      </Card>
    </div>
  );
}

function UnitForm({ t, onClose, onSaved }: { t: EquipmentTranslate; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const siteList = useResource(() => loadSites(), []);
  const [siteId, setSiteId] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<UnitKind>('portal');
  const [protocol, setProtocol] = useState<UnitProtocol>('http');
  const [endpoint, setEndpoint] = useState('');
  const [programs, setPrograms] = useState('express;Экспресс;300\nfull;Комплекс;900');
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      await createUnit({
        siteId,
        code,
        name,
        kind,
        protocol,
        ...(protocol === 'http' ? { endpoint } : {}),
        ...(protocol === 'mqtt' ? {} : { programs: parsePrograms(programs) }),
      });
      toast.ok(t('equipment.created'), code);
      onSaved();
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      title={t('equipment.form.title')}
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <Button tone="primary" onClick={() => void submit()} disabled={busy || !siteId || !code || !name}>
            {t('equipment.form.create')}
          </Button>
          <Button onClick={onClose} disabled={busy}>
            {t('equipment.form.cancel')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t('equipment.form.site')} required>
          <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="" />
            {(siteList.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('equipment.form.code')} required>
          <TextInput value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label={t('equipment.form.name')} required>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('equipment.form.kind')}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as UnitKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`equipment.kind.${k}` as EquipmentKey)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('equipment.form.protocol')}>
          <Select value={protocol} onChange={(e) => setProtocol(e.target.value as UnitProtocol)}>
            {PROTOCOLS.map((p) => (
              <option key={p} value={p}>
                {t(`equipment.form.protocol.${p}` as EquipmentKey)}
              </option>
            ))}
          </Select>
        </Field>
        {protocol === 'http' ? (
          <Field label={t('equipment.form.endpoint')} required>
            <TextInput value={endpoint} placeholder="http://10.0.0.21:8080" onChange={(e) => setEndpoint(e.target.value)} />
          </Field>
        ) : null}
        {protocol !== 'mqtt' ? (
          <Field label={t('equipment.form.programs')} hint={t('equipment.form.programsHint')}>
            <TextArea rows={4} value={programs} onChange={(e) => setPrograms(e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Drawer>
  );
}

export default function EquipmentModule({ user, title, siteId }: ModuleViewProps) {
  const t = useMemo(() => makeEquipmentT(user.locale === 'kk' ? 'kk' : 'ru'), [user.locale]);
  const toast = useToast();
  const [reloadKey, setReloadKey] = useState(0);
  const [openId, setOpenId] = useState('');
  const [creating, setCreating] = useState(false);

  const canManage = allows(user, 'admin.manage');
  const canStart = allows(user, 'order.create');
  const units = useResource(() => fetchUnits(siteId ?? undefined), [siteId, reloadKey]);

  async function recompute(): Promise<void> {
    try {
      await runForecast();
      toast.ok(t('equipment.recomputed'));
      setReloadKey((v) => v + 1);
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    }
  }

  const columns: ReadonlyArray<Column<Unit>> = [
    { key: 'code', header: t('equipment.col.code'), render: (u) => u.code, width: '6rem' },
    { key: 'name', header: t('equipment.col.name'), render: (u) => `${u.name} · ${t(`equipment.kind.${u.kind}` as EquipmentKey)}` },
    {
      key: 'state',
      header: t('equipment.col.state'),
      render: (u) => (
        <StatusBadge tone={u.lastState === 'fault' ? 'danger' : u.lastState === 'running' ? 'brand' : 'neutral'}>
          {t(stateKey(u.lastState))}
        </StatusBadge>
      ),
    },
    {
      key: 'readings',
      header: t('equipment.col.readings'),
      render: (u) =>
        u.readings.length === 0 ? '—' : u.readings.map((r) => `${r.metric} ${Number(r.value.toFixed(2))}`).join(', '),
    },
    {
      key: 'risk',
      header: t('equipment.col.risk'),
      render: (u) =>
        u.forecast ? (
          <div className="flex flex-col gap-0.5">
            <StatusBadge tone={riskTone(u.forecast.risk)}>{percent(u.forecast.risk)}</StatusBadge>
            {u.forecast.driver ? (
              <span className="text-xs text-ink-muted">
                {t('equipment.risk.driver', { driver: u.forecast.driver })}
                {u.forecast.daysToLimit !== null ? ` · ${t('equipment.risk.days', { days: u.forecast.daysToLimit })}` : ''}
              </span>
            ) : null}
          </div>
        ) : (
          '—'
        ),
    },
    { key: 'seen', header: t('equipment.col.seen'), render: (u) => <Moment iso={u.lastSeenAt} /> },
  ];

  const open = (units.data ?? []).find((u) => u.id === openId);

  return (
    <ModulePage title={title} subtitle={t('equipment.subtitle')}>
      {canManage ? (
        <Toolbar>
          <Button tone="primary" onClick={() => setCreating(true)}>
            {t('equipment.add')}
          </Button>
          <Button onClick={() => void recompute()}>{t('equipment.recompute')}</Button>
        </Toolbar>
      ) : null}

      {creating ? (
        <UnitForm
          t={t}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            setReloadKey((v) => v + 1);
          }}
        />
      ) : null}

      {open ? (
        <Drawer title={`${open.code} — ${open.name}`} onClose={() => setOpenId('')}>
          <UnitDetails t={t} unit={open} canStart={canStart} onChanged={() => setReloadKey((v) => v + 1)} />
        </Drawer>
      ) : null}

      <Resourceful resource={units}>
        {(rows) => (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(u) => u.id}
            onRowClick={(u) => setOpenId(u.id)}
            activeKey={openId}
            empty={t('equipment.empty')}
            emptyReason={t('equipment.emptyReason')}
            loading={units.loading}
          />
        )}
      </Resourceful>
    </ModulePage>
  );
}
